import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfCtaText, sfCtaUrl, sfHeadline, sfSub } from './sectionHelpers';

const CtaBanner: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || section.variant || 'primary_bg';

  const bgStyle: React.CSSProperties = {
    padding: '80px 24px',
    textAlign: 'center',
    position: 'relative',
  };

  const hasImage = !!c.background_image;

  // Custom colour controls (Python reads these)
  const customBg    = c.background_color;
  const customColor = c.text_color;

  if (hasImage) {
    bgStyle.backgroundImage = `url(${c.background_image})`;
    bgStyle.backgroundSize = 'cover';
    bgStyle.backgroundPosition = 'center';
    bgStyle.color = customColor || '#fff';
  } else if (customBg) {
    // Any variant can supply an explicit background_color (teal-fun, brand, etc.)
    bgStyle.background = customBg;
    bgStyle.color = customColor || '#fff';
  } else if (variant === 'primary_bg') {
    bgStyle.background = 'var(--brand-primary, #1a365d)';
    bgStyle.color = customColor || '#fff';
  } else if (variant === 'gradient_bg') {
    const gFrom = c.gradient_from || 'var(--brand-primary, #1a365d)';
    const gTo   = c.gradient_to   || 'var(--brand-secondary, #2563eb)';
    const gDir  = c.gradient_direction || '135deg';
    bgStyle.background = `linear-gradient(${gDir}, ${gFrom} 0%, ${gTo} 100%)`;
    bgStyle.color = customColor || '#fff';
  } else if (variant === 'teal-fun') {
    bgStyle.background = 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #0f766e 100%)';
    bgStyle.color = customColor || '#fff';
    bgStyle.borderRadius = 24;
  } else {
    // Fallback
    bgStyle.background = 'var(--brand-primary, #1a365d)';
    bgStyle.color = '#fff';
  }

  // Button: white bg + primary text on coloured backgrounds, primary bg on image overlays
  const isDark = bgStyle.color === '#fff' || (typeof bgStyle.color === 'string' && bgStyle.color.startsWith('#fff'));
  const btnColor = hasImage ? '#fff' : isDark ? 'var(--brand-primary, #1a365d)' : '#fff';
  const btnBg   = hasImage ? 'var(--brand-primary, #1a365d)' : isDark ? '#fff' : 'var(--brand-primary, #1a365d)';

  return (
    <section className="sb-render sb-render-cta-banner" style={bgStyle}>
      {hasImage && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: 'inherit' }} />
      )}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto' }}>
        <InlineEditText
          tag="h2"
          controlKey="headline"
          style={{ fontSize: '2.5rem', marginBottom: 16, color: 'inherit', letterSpacing: '-0.025em' }}
        >
          {sfHeadline(c, 'Ready to Get Started?')}
        </InlineEditText>
        <InlineEditText
          tag="p"
          controlKey="subheadline"
          style={{ fontSize: '1.125rem', marginBottom: 32, opacity: 0.9 }}
        >
          {sfSub(c, 'Take the next step today.')}
        </InlineEditText>
        <a href={sfCtaUrl(c)} className="sb-cta-btn" style={{
          display: 'inline-block', padding: '18px 40px', borderRadius: 'var(--brand-btn-radius, 6px)',
          background: btnBg, color: btnColor, fontWeight: 600, textDecoration: 'none', fontSize: '1.125rem',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <InlineEditText tag="span" controlKey="cta_text">
            {sfCtaText(c, 'Get Started')}
          </InlineEditText>
        </a>
      </div>
    </section>
  );
};

export default CtaBanner;
